import React from 'react';
import './StoreBadges.css';

/**
 * "Get it on Google Play" / "Download on the App Store" buttons.
 *
 * Each badge renders only when its store URL is set — same rule as the
 * footer social icons, so we never ship a button pointing nowhere. URLs
 * come from CMS global settings (Website Admin → General Settings →
 * "Mobile app"); the Play Store URL falls back to the known package id.
 *
 * The glyphs are inline SVG (no external asset, CSP-safe). They follow the
 * official badge layout — dark pill, store mark, "small line / Store name" —
 * closely enough to be unmistakable; drop in the official artwork later if
 * strict brand compliance is needed.
 */

const DEFAULT_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.visionexploit.studentsapp';

const GooglePlayGlyph = () => (
  <svg className="store-badge-glyph" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path d="M3.6 2.3c-.28.3-.44.75-.44 1.34v16.72c0 .59.16 1.04.45 1.33l.09.08 9.36-9.36v-.22L3.7 2.22l-.1.08z" fill="#00C3FF" />
    <path d="M16.5 15.1l-3.4-3.4v-.22l3.4-3.4.08.05 4.05 2.3c1.16.66 1.16 1.73 0 2.4l-4.05 2.3-.08.02z" fill="#FFCE00" />
    <path d="M16.58 15.05L13.1 11.6l-9.5 9.5c.38.4 1.02.45 1.74.05l11.24-6.1z" fill="#00DE7A" />
    <path d="M16.58 8.15L5.34 2.05C4.62 1.65 3.98 1.7 3.6 2.1l9.5 9.5 3.48-3.45z" fill="#FF3A44" />
  </svg>
);

const AppleGlyph = () => (
  <svg className="store-badge-glyph" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="currentColor"
      d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
    />
  </svg>
);

const StoreBadges = ({ playStoreUrl, appStoreUrl, className = '' }) => {
  const play = (playStoreUrl || DEFAULT_PLAY_URL || '').trim();
  const apple = (appStoreUrl || '').trim();

  if (!play && !apple) return null;

  return (
    <div className={`store-badges ${className}`.trim()}>
      {play && (
        <a
          className="store-badge"
          href={play}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
        >
          <GooglePlayGlyph />
          <span className="store-badge-text">
            <span className="store-badge-eyebrow">Get it on</span>
            <span className="store-badge-name">Google Play</span>
          </span>
        </a>
      )}
      {apple && (
        <a
          className="store-badge"
          href={apple}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download on the App Store"
        >
          <AppleGlyph />
          <span className="store-badge-text">
            <span className="store-badge-eyebrow">Download on the</span>
            <span className="store-badge-name">App Store</span>
          </span>
        </a>
      )}
    </div>
  );
};

export default StoreBadges;
